import React from 'react';

const PLACEHOLDER_PRIVACY_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=bdf3687f-046c-403a-8855-7d3c6f53a3e6';
const PLACEHOLDER_TERMS_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=a3abf51d-e155-4c4d-8a02-b2b7f82413d2';

const section = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '120px 32px',
};

const bodyFont = "'Cormorant', 'Cormorant Garamond', Georgia, serif";

const LandingPageView = ({ onBeginTrial, onSignIn, onNavigate }) => {
  return (
    <div className="velvet-void" style={{ color: '#F5E6D3' }} data-testid="landing-page">

      {/* ── STICKY HEADER ─────────────────────────────────────────────── */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(8, 6, 8, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{
          fontFamily: bodyFont,
          fontStyle: 'italic',
          fontSize: '18px',
          color: 'rgba(245, 230, 211, 0.7)',
        }}>
          Eunoia Presence
        </span>
        <button
          onClick={onSignIn}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(245, 230, 211, 0.55)',
            fontSize: '14px',
            fontFamily: bodyFont,
            fontStyle: 'italic',
            padding: '0',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          Sign in
        </button>
      </header>

      {/* ── SECTION 1 — Hero ───────────────────────────────────────────── */}
      <section style={{
        ...section,
        minHeight: '100vh',
        justifyContent: 'center',
        padding: '60px 32px 0',
      }} className="fade-in">
        <div
          className="breathing-sphere"
          style={{
            marginBottom: '64px',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            willChange: 'transform',
          }}
        />

        <h1 style={{
          fontFamily: bodyFont,
          fontStyle: 'italic',
          fontWeight: 400,
          fontSize: 'clamp(26px, 5vw, 34px)',
          lineHeight: 1.5,
          color: 'rgba(245, 230, 211, 0.88)',
          textAlign: 'center',
          letterSpacing: '-0.01em',
          marginBottom: '64px',
          maxWidth: '480px',
        }}>
          A quiet presence for the<br />
          moments when you need<br />
          to be heard.
        </h1>

        <div style={{ width: '100%', maxWidth: '420px' }}>
          <button
            onClick={onBeginTrial}
            className="velvet-btn"
            data-testid="begin-trial-btn"
            style={{
              fontFamily: bodyFont,
              fontSize: '1rem',
              letterSpacing: '0.02em',
            }}
          >
            Begin your 7 day free trial
          </button>
        </div>
      </section>

      {/* ── SECTION 2 — What this is ───────────────────────────────────── */}
      <section style={section}>
        <div style={{ maxWidth: '520px', textAlign: 'center' }}>
          <p style={paragraphStyle(20)}>
            Eunoia is a quiet companion<br />
            for the moments that are<br />
            hard to explain.
          </p>

          <p style={paragraphStyle(20)}>
            Not therapy. Not a chatbot.<br />
            Something closer to a friend<br />
            who is always present,<br />
            never rushed, and genuinely<br />
            curious about you.
          </p>

          <p style={paragraphStyle(0)}>
            There is no right way<br />
            to be here.
          </p>
        </div>
      </section>

      {/* ── SECTION 3 — Who this is for ───────────────────────────────── */}
      <section style={{ ...section, paddingTop: '80px' }}>
        <div style={{ maxWidth: '520px', textAlign: 'center' }}>
          <p style={paragraphStyle(56)}>
            You don't have to be in<br />
            crisis to deserve to be heard.<br />
            You just have to be human.
          </p>

          {/* amber divider */}
          <div style={{
            width: '40px',
            height: '1px',
            background: 'rgba(245, 179, 100, 0.5)',
            margin: '0 auto 56px',
          }} />

          <p style={paragraphStyle(40)}>
            This is for the person who<br />
            knows themselves pretty well<br />
            and still can't shake the<br />
            feeling that something's missing.
          </p>

          <p style={paragraphStyle(40)}>
            For the one who's fine, mostly.<br />
            Except at 3am.
          </p>

          <p style={paragraphStyle(0)}>
            For anyone carrying something<br />
            real with nowhere quiet<br />
            to put it down.
          </p>
        </div>
      </section>

      {/* ── SECTION 4 — A glimpse ─────────────────────────────────────── */}
      <section style={{ ...section, paddingTop: '100px', paddingBottom: '100px' }}>
        <div style={{
          width: '100%',
          maxWidth: '480px',
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(245, 179, 100, 0.1)',
          borderRadius: '16px',
          padding: '40px 28px',
        }}>
          <UserBubble>
            how does this work?{'\n'}i dont know what to say.
          </UserBubble>

          <EunoiaBubble>
            You don't have to know what to say... that's actually a pretty good
            place to start. Most people arrive here carrying something — sometimes
            it's clear, sometimes it's just a feeling they can't quite name yet.
            Both are fine. There's no right way to be here. So... what's present
            for you right now?
          </EunoiaBubble>

          <UserBubble>
            ive been kind of feeling like{'\n'}theres no way i can get ahead in life.
          </UserBubble>

          <EunoiaBubble>
            Yeah... that feeling of running in place, no matter how hard you try.
            It's exhausting in a way that's hard to explain to people who haven't
            felt it.
          </EunoiaBubble>

          <p style={{
            fontFamily: bodyFont,
            fontStyle: 'italic',
            fontSize: '13px',
            color: 'rgba(245, 230, 211, 0.3)',
            textAlign: 'center',
            marginTop: '32px',
            marginBottom: 0,
          }}>
            A real conversation with Eunoia.
          </p>
        </div>
      </section>

      {/* ── SECTION 5 — Quiet trust ───────────────────────────────────── */}
      <section style={{ ...section, paddingTop: '80px', paddingBottom: '80px' }}>
        <div style={{ maxWidth: '420px', textAlign: 'center' }}>
          <p style={trustLine}>
            Your conversations are private.<br />
            They are never sold or shared.
          </p>
          <p style={trustLine}>
            This is not therapy or a<br />
            replacement for human connection.
          </p>
          <p style={{ ...trustLine, marginBottom: 0 }}>
            No pressure. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ── SECTION 6 — Crisis line ───────────────────────────────────── */}
      <section style={{ ...section, paddingTop: '0', paddingBottom: '100px' }}>
        <div style={{ maxWidth: '420px', textAlign: 'center' }}>
          <div style={{
            width: '100%',
            height: '1px',
            background: 'rgba(245, 179, 100, 0.2)',
            marginBottom: '40px',
          }} />
          <p style={{
            fontFamily: bodyFont,
            fontStyle: 'italic',
            fontSize: '13px',
            lineHeight: 1.8,
            color: 'rgba(245, 230, 211, 0.35)',
            marginBottom: '12px',
          }}>
            If you are in acute crisis,<br />
            please reach out to someone who can help right now.
          </p>
          <a
            href="https://988lifeline.org"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: bodyFont,
              fontStyle: 'italic',
              fontSize: '13px',
              color: 'rgba(245, 179, 100, 0.5)',
              textDecoration: 'none',
            }}
          >
            988 Suicide and Crisis Lifeline — call or text 988
          </a>
        </div>
      </section>

      {/* ── SECTION 7 — Bottom CTA ────────────────────────────────────── */}
      <section style={{ ...section, paddingTop: '80px', paddingBottom: '140px' }}>
        <div
          className="breathing-sphere"
          style={{
            width: '64px',
            height: '64px',
            marginBottom: '48px',
            opacity: 0.7,
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            willChange: 'transform',
          }}
        />

        <div style={{ width: '100%', maxWidth: '420px', marginBottom: '20px' }}>
          <button
            onClick={onBeginTrial}
            className="velvet-btn"
            data-testid="begin-trial-btn-bottom"
            style={{
              fontFamily: bodyFont,
              fontSize: '1rem',
              letterSpacing: '0.02em',
            }}
          >
            Begin your 7 day free trial
          </button>
        </div>

        <p style={{
          fontFamily: bodyFont,
          fontStyle: 'italic',
          fontSize: '13px',
          color: 'rgba(245, 230, 211, 0.35)',
          margin: 0,
          letterSpacing: '0.02em',
        }}>
          7 days free. Then $7.99/month. Cancel anytime.
        </p>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────── */}
      <footer style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '24px',
        padding: '28px 32px 40px',
        borderTop: '1px solid rgba(245, 230, 211, 0.06)',
      }}>
        <button
          onClick={() => onNavigate && onNavigate('about')}
          style={footerLinkStyle}
        >
          About
        </button>
        <a
          href={PLACEHOLDER_PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={footerLinkStyle}
        >
          Privacy Policy
        </a>
        <a
          href={PLACEHOLDER_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={footerLinkStyle}
        >
          Terms of Service
        </a>
      </footer>

    </div>
  );
};

/* ── Sub-components ─────────────────────────────────────────────────────── */

const UserBubble = ({ children }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '20px',
  }}>
    <div style={{
      maxWidth: '78%',
      background: 'rgba(245, 179, 100, 0.1)',
      border: '1px solid rgba(245, 179, 100, 0.18)',
      borderRadius: '18px 18px 4px 18px',
      padding: '14px 18px',
      fontFamily: bodyFont,
      fontStyle: 'italic',
      fontSize: '15px',
      lineHeight: 1.6,
      color: 'rgba(245, 230, 211, 0.75)',
      whiteSpace: 'pre-line',
    }}>
      {children}
    </div>
  </div>
);

const EunoiaBubble = ({ children }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '20px',
  }}>
    <div style={{
      maxWidth: '85%',
      borderLeft: '2px solid rgba(245, 179, 100, 0.4)',
      paddingLeft: '16px',
      fontFamily: bodyFont,
      fontStyle: 'italic',
      fontSize: '15px',
      lineHeight: 1.75,
      color: 'rgba(245, 230, 211, 0.6)',
    }}>
      {children}
    </div>
  </div>
);

/* ── Style helpers ──────────────────────────────────────────────────────── */

function paragraphStyle(marginBottomPx) {
  return {
    fontFamily: "'Cormorant', 'Cormorant Garamond', Georgia, serif",
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: 'clamp(19px, 3.5vw, 23px)',
    lineHeight: 1.75,
    color: 'rgba(245, 230, 211, 0.78)',
    marginBottom: `${marginBottomPx}px`,
    letterSpacing: '0.01em',
  };
}

const trustLine = {
  fontFamily: "'Cormorant', 'Cormorant Garamond', Georgia, serif",
  fontStyle: 'italic',
  fontSize: '14px',
  lineHeight: 1.9,
  color: 'rgba(245, 230, 211, 0.4)',
  marginBottom: '32px',
  letterSpacing: '0.02em',
};

const footerLinkStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'rgba(245, 230, 211, 0.35)',
  fontFamily: "'Cormorant', 'Cormorant Garamond', Georgia, serif",
  fontStyle: 'italic',
  fontSize: '14px',
  lineHeight: 1,
  letterSpacing: '0.04em',
  padding: '0',
  margin: '0',
  textDecoration: 'none',
  display: 'block',
};

export default LandingPageView;
